/**
 * Pure data-shaping for the monitored-event review charts (OMON-201).
 *
 * Deliberately free of any Chart.js import so it is unit-testable in Node:
 * chart-kit registers chartjs-plugin-zoom (→ hammerjs) at module load, which
 * needs a DOM. monitored-chart.ts (the renderer) imports these helpers + the
 * point types and applies colours/config; this module owns the windowing,
 * filtering, day-bucketing and threshold logic.
 */

import type { MonitoredEventInfo, NarrativeEntry } from "../types";

export const WINDOW_DAYS = 14;

/**
 * The shared review window for the monitored-event charts.
 *
 * Right edge = today. Left edge = the OLDEST recording across ALL the
 * trackables passed in, not per chart — the trackables were not all started at
 * the same point in the plan's life, and giving each its own left edge would
 * put charts side by side on different scales, which is the misreading this
 * whole change exists to remove. A trackable that started late simply shows
 * empty space at the left, which is the honest rendering of "no data yet".
 *
 * Not the plan period: that trails months of empty future before any of it has
 * happened, and empty past before anyone recorded anything.
 *
 * It used to be a trailing 14 days, which OMON-201 justified as matching "the
 * iOS carer app's per-shift 'today' scope" — a coupling to a client app that is
 * now frozen and being retired (OMON-259).
 *
 * Falls back to a trailing WINDOW_DAYS when nothing has been recorded at all,
 * so an empty plan still renders an axis.
 */
export function windowForRecordings(
  narrative: NarrativeEntry[],
  codes: string[],
  today: Date = new Date(),
): Window {
  const wanted = new Set(codes);
  let oldest = "";
  // ADR-0035 am.9b: a not-applicable answer is not data, so it never sets
  // the charts' left edge either.
  for (const e of plottableRecordings(narrative)) {
    if (!wanted.has(e.code)) continue;
    if (!oldest || e.date < oldest) oldest = e.date;
  }
  if (!oldest) return windowFor(today);

  const [y, m, d] = oldest.slice(0, 10).split("-").map(Number);
  const start = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
  if (end < start) return windowFor(today);

  const days: string[] = [];
  for (const cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) {
    days.push(isoDay(new Date(cur)));
  }
  return { startMs: start.getTime(), endMs: end.getTime(), days };
}

/** How much of the review window the monitored charts OPEN on (OMON-308). */
export const VIEW_MONTHS = 1;

/**
 * OMON-308: the view the monitored charts open on — the latest month, right
 * edge on today. Matches the Connect swimlane's live-tail shape, which is what
 * anyone reading a per-shift obligation actually wants: the recent record, with
 * the history a zoom-out away.
 *
 * ☠️ This is the VIEW, not the data window. `windowForRecordings` still spans
 * every recording, because it also drives day-bucketing (`countsByDay`) and the
 * pan/zoom limits — narrowing it here would delete the older half of the series
 * instead of merely scrolling past it.
 *
 * No "now" marker is wanted on these charts, unlike the trajectory ones: the
 * right edge IS today, so the edge already carries that meaning (see
 * now-marker.ts for the rule).
 *
 * If the last month happens to hold no recordings — an event nobody has logged
 * for a while, or an archived plan — the view slides back to end at the newest
 * recording instead, at the same width. An empty chart reads as broken, and
 * "nothing here" is better said by the space around a real reading than by a
 * blank rectangle.
 */
export function latestMonthView(win: Window, recordingDates: number[] = []): { min: number; max: number } {
  const monthBefore = (end: number) => {
    const d = new Date(end);
    d.setMonth(d.getMonth() - VIEW_MONTHS);
    return d.getTime();
  };
  const clamp = (end: number) => ({ min: Math.max(win.startMs, monthBefore(end)), max: end });

  const view = clamp(win.endMs);
  if (view.min <= win.startMs) return { min: win.startMs, max: win.endMs };
  if (recordingDates.some((t) => t >= view.min && t <= view.max)) return view;

  const newest = recordingDates.length ? Math.max(...recordingDates) : 0;
  if (!newest) return view;
  return clamp(Math.min(newest, win.endMs));
}

export type PointKind = "value" | "bool" | "count" | "occurrence";

export interface PointMeta {
  kind: PointKind;
  /** Main value text for the tooltip, e.g. "97", "Yes", "3 occurrences". */
  label: string;
  note?: string;
  performerName?: string;
  /** ISO timestamp (or YYYY-MM-DD day, for count points) of the recording. */
  when: string;
  /** count points only: how many of that day's occurrences had a note. */
  noteCount?: number;
}

export interface ChartPoint {
  x: number; // epoch ms
  y: number;
  _meta: PointMeta;
}

export interface Threshold {
  value: number;
  kind: "target" | "escalate";
  /**
   * Which side is the concerning one. Absent ⇒ "above" (the historical
   * behaviour). A target is always "keep it at or under", so targets stay
   * "above" regardless.
   */
  direction?: "above" | "below";
}

/**
 * Is this reading a breach of the threshold?
 *
 * ☠️ The ONE comparison. It used to be an inline `p.y > thr.value` in two
 * chart renderers, which silently assumed every fact is worrying when it
 * RISES. For a "below" fact — fluid intake, meals eaten, hours slept — that
 * painted every GOOD day red and every bad day normal, i.e. exactly inverted.
 * Keep it here so a third renderer cannot reintroduce the assumption.
 */
export function isBreach(y: number, thr: Threshold | null): boolean {
  if (!thr) return false;
  return thr.direction === "below" ? y < thr.value : y > thr.value;
}

export interface Window {
  startMs: number;
  endMs: number;
  /** YYYY-MM-DD keys, oldest first. */
  days: string[];
}

export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // Day-only keys (count points) have no time component → show the date only.
  const hasTime = iso.length > 10;
  return d.toLocaleString("en-GB", hasTime
    ? { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "numeric", month: "short", year: "numeric" });
}

/** The trailing `n`-day window ending today (local calendar days). */
export function windowFor(today: Date, n = WINDOW_DAYS): Window {
  const days: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    days.push(isoDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i)));
  }
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (n - 1), 0, 0, 0);
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
  return { startMs: start.getTime(), endMs: end.getTime(), days };
}

/**
 * ADR-0035 am.9b: drop the carers' "Not applicable on this shift" answers.
 * They answer the item but are NOT data: plotted, a boolean would read as
 * "No", a tally as one more occurrence, a rating as a blank or a zero. Every
 * chart, count and window edge reads through this. The diary timeline does
 * not — it is a per-item history, and shows them as "Not applicable — …".
 */
export function plottableRecordings(recs: NarrativeEntry[]): NarrativeEntry[] {
  return recs.filter((e) => e.kind !== "not-applicable");
}

/** Recordings for one fact that fall inside the window, in date order.
 *  Includes not-applicable answers — charts pass the result through
 *  `plottableRecordings`. */
export function windowedRecordings(narrative: NarrativeEntry[], odCode: string, win: Window): NarrativeEntry[] {
  return narrative
    .filter((e) => e.code === odCode && e.date)
    .filter((e) => { const t = new Date(e.date).getTime(); return t >= win.startMs && t <= win.endMs; })
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function thresholdFor(m: MonitoredEventInfo): Threshold | null {
  if (m.escalate === true && typeof m.escalationThreshold === "number") {
    return {
      value: m.escalationThreshold,
      kind: "escalate",
      direction: m.escalationDirection === "below" ? "below" : "above",
    };
  }
  if (typeof m.targetValue === "number") return { value: m.targetValue, kind: "target" };
  return null;
}

/** Occurrence counts per window day (tally), aligned to win.days. */
export function countsByDay(win: Window, recs: NarrativeEntry[]): number[] {
  const idx = new Map(win.days.map((d, i) => [d, i]));
  const out = new Array(win.days.length).fill(0);
  // Belt and braces (am.9b): a not-applicable answer is never an occurrence.
  for (const r of plottableRecordings(recs)) {
    const i = idx.get(r.date.slice(0, 10));
    if (i !== undefined) out[i] += 1;
  }
  return out;
}

/** Day keys (within window) that have at least one noted occurrence (tally). */
export function noteDaysSet(recs: NarrativeEntry[]): Set<string> {
  const s = new Set<string>();
  for (const r of recs) if (r.note?.trim()) s.add(r.date.slice(0, 10));
  return s;
}

/** Notes recorded on a given day (count points report how many). */
export function noteCountForDay(recs: NarrativeEntry[], day: string): number {
  return recs.filter((r) => r.date.slice(0, 10) === day && r.note?.trim()).length;
}

/**
 * Is this trackable a diary? Keyed on the recording KIND, never on the
 * daily-log doc id: the OD has already been re-created once (_migratedFrom)
 * and a plan may one day carry a second diary (a night log). Any code that
 * needs "the diary" must ask this, not compare identities.
 */
export function isDiary(m: MonitoredEventInfo): boolean {
  // OMON-232: an `incident` (ABC chart) is a structured log. Each entry also
  // carries a readable note summary (the hybrid), so the log timeline, the
  // logs tab and the "N logs" count all treat it as one, with no chart.
  return m.recordingKind === "diary" || m.recordingKind === "incident";
}

/** The log timeline's caption names the log it is: an incident log (OMON-232,
 *  the ABC chart) is not the Shift log, though both render as a timeline. */
export function timelineCaption(recordingKind?: string): string {
  return `${recordingKind === "incident" ? "Incident log" : "Shift log"} · newest day first.`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The plan card's event summary, split the way the tabs split it (Ed,
 * 2026-09-30): "5 monitored events · 3 logs". Seven live plans carried two or
 * three diary-kind events (Shift log, ABC chart, sleep pattern, Plan for the
 * Day), so "8 monitored events" over a "Monitored events · 5" tab read as three
 * missing. Returns the parts to join; empty when there are no events.
 */
export function planEventSummaryParts(events: MonitoredEventInfo[]): string[] {
  const logs = events.filter(isDiary).length;
  const monitored = events.length - logs;
  const parts: string[] = [];
  if (monitored > 0) parts.push(plural(monitored, "monitored event", "monitored events"));
  if (logs > 0) parts.push(plural(logs, "log", "logs"));
  return parts;
}

/** The diary tab's label: the log's own title when there is one ("Shift
 *  log"), "Logs" with a count when there are several, so nothing hides behind
 *  a name that describes only one of them. `count` is null for a single log
 *  (its title says what it is). */
export function diaryTabLabel(events: MonitoredEventInfo[]): { text: string; count: number | null } {
  const logs = events.filter(isDiary);
  return logs.length === 1 ? { text: logs[0].title, count: null } : { text: "Logs", count: logs.length };
}
